import { Box } from "@mui/material";
import React from "react";
import { UI, btnSx } from "./ActionDrawer";

/* Table, tags and page furniture from the internal-dash prototype. */

export const Tag = ({ tone = "i", children }) => {
  const tones = {
    b: { bg: UI.redBg, fg: UI.red },
    s: { bg: UI.amberBg, fg: UI.amber },
    i: { bg: UI.bg3, fg: UI.ink3 },
    g: { bg: UI.greenBg, fg: UI.green },
    bl: { bg: UI.blueBg, fg: UI.blue },
    stage: { bg: UI.p50, fg: UI.pDark },
  };
  const t = tones[tone] || tones.i;
  return (
    <Box
      component="span"
      sx={{
        fontSize: 11,
        padding: "2px 7px",
        borderRadius: "3px",
        fontWeight: 500,
        letterSpacing: ".2px",
        whiteSpace: "nowrap",
        bgcolor: t.bg,
        color: t.fg,
        display: "inline-block",
      }}
    >
      {children}
    </Box>
  );
};

export const Oid = ({ children }) => (
  <Box
    component="span"
    sx={{ fontFamily: UI.mono, fontSize: 12, color: UI.p, fontWeight: 500 }}
  >
    {children}
  </Box>
);

export const Sub2 = ({ children }) => (
  <Box
    component="span"
    sx={{ display: "block", color: UI.ink3, fontSize: 11, marginTop: "2px" }}
  >
    {children}
  </Box>
);

export const Notice = ({ children }) => (
  <Box
    sx={{
      bgcolor: UI.amberBg,
      border: "1px solid",
      borderColor: UI.amberLine,
      color: UI.amberInk,
      fontSize: 12,
      padding: "10px 13px",
      borderRadius: "4px",
      marginBottom: "18px",
      "& b": { fontWeight: 500 },
    }}
  >
    {children}
  </Box>
);

export const Restrict = ({ what }) => (
  <Box
    sx={{
      bgcolor: UI.bg2,
      border: "1px dashed",
      borderColor: UI.lineIn,
      borderRadius: "8px",
      padding: "24px 18px",
      textAlign: "center",
      color: UI.ink3,
      fontSize: 12,
    }}
  >
    <Box
      sx={{ color: UI.ink2, fontWeight: 500, fontSize: 13, marginBottom: "3px" }}
    >
      {what} is restricted for your role
    </Box>
    Enforced server side, not by hiding the panel.
  </Box>
);

export const PageHead = ({ title, lede, kpis }) => (
  <Box>
    <Box
      component="h1"
      sx={{ fontSize: 22, fontWeight: 500, margin: "0 0 4px", color: UI.ink }}
    >
      {title}
    </Box>
    {lede ? (
      <Box
        sx={{ color: UI.ink3, margin: "0 0 22px", fontSize: 13, maxWidth: 760 }}
      >
        {lede}
      </Box>
    ) : null}
    {kpis && kpis.length ? (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            md: `repeat(${Math.min(kpis.length, 4)}, 1fr)`,
          },
          gap: "12px",
          marginBottom: "26px",
        }}
      >
        {kpis.map((k) => (
          <Box
            key={k.label}
            sx={{
              bgcolor: UI.surface,
              border: "1px solid",
              borderColor: UI.line,
              borderRadius: "8px",
              padding: "14px 16px",
            }}
          >
            <Box
              sx={{
                fontSize: 26,
                fontWeight: 500,
                lineHeight: 1.1,
                color: k.alarm ? UI.red : UI.ink,
              }}
            >
              {k.value === undefined || k.value === null ? "—" : k.value}
            </Box>
            <Box sx={{ fontSize: 12, color: UI.ink3, marginTop: "3px" }}>
              {k.label}
            </Box>
          </Box>
        ))}
      </Box>
    ) : null}
  </Box>
);

export const SectionTitle = ({ children, sub }) => (
  <Box>
    <Box
      sx={{ fontSize: 15, fontWeight: 500, margin: "0 0 4px", color: UI.ink }}
    >
      {children}
    </Box>
    {sub ? (
      <Box sx={{ fontSize: 12, color: UI.ink3, margin: "0 0 14px" }}>{sub}</Box>
    ) : null}
  </Box>
);

export const Empty = ({ title, sub }) => (
  <Box
    sx={{
      padding: "40px 18px",
      textAlign: "center",
      color: UI.ink3,
      fontSize: 13,
      bgcolor: UI.surface,
      border: "1px solid",
      borderColor: UI.line,
      borderRadius: "8px",
      marginBottom: "28px",
    }}
  >
    <Box sx={{ color: UI.ink, fontWeight: 500, fontSize: 14, marginBottom: "4px" }}>
      {title}
    </Box>
    {sub}
  </Box>
);

export const FilterChips = ({ options, value, onChange }) => (
  <Box
    sx={{
      display: "flex",
      gap: "8px",
      flexWrap: "wrap",
      alignItems: "center",
      marginBottom: "14px",
    }}
  >
    {options.map((o) => {
      const key = typeof o === "string" ? o : o.value;
      const label = typeof o === "string" ? o : o.label;
      const on = value === key;
      return (
        <Box
          component="button"
          type="button"
          key={key}
          onClick={() => onChange(key)}
          sx={{
            border: "1px solid",
            borderColor: on ? UI.p : UI.line,
            bgcolor: on ? UI.p : UI.surface,
            color: on ? UI.surface : UI.ink2,
            fontWeight: on ? 500 : 400,
            borderRadius: "14px",
            padding: "5px 13px",
            fontSize: 12,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          {label}
        </Box>
      );
    })}
  </Box>
);

/** Panel used on detail pages, the prototype's .card */
export const Card = ({ title, action, children, sx }) => (
  <Box
    sx={{
      bgcolor: UI.surface,
      border: "1px solid",
      borderColor: UI.line,
      borderRadius: "8px",
      marginBottom: "18px",
      ...sx,
    }}
  >
    {title ? (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "10px",
          padding: "11px 15px",
          borderBottom: "1px solid",
          borderBottomColor: UI.line,
          bgcolor: UI.bg2,
          borderRadius: "8px 8px 0 0",
        }}
      >
        <Box sx={{ fontSize: 13, fontWeight: 500, color: UI.ink }}>{title}</Box>
        {action}
      </Box>
    ) : null}
    <Box sx={{ padding: "15px" }}>{children}</Box>
  </Box>
);

/** Label over value, as used in a detail page header strip. */
export const Stat = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Box
      sx={{
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: ".5px",
        color: UI.ink3,
        fontWeight: 500,
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        fontSize: 14,
        fontWeight: 500,
        color: UI.ink,
        marginTop: "3px",
        overflowWrap: "break-word",
      }}
    >
      {value === undefined || value === null || value === "" ? "—" : value}
    </Box>
  </Box>
);

/** Label and value on their own line inside a Card. */
export const DefRow = ({ label, value, last }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      gap: "12px",
      padding: "9px 0",
      borderBottom: last ? 0 : "1px solid",
      borderBottomColor: UI.line,
      fontSize: 13,
    }}
  >
    <Box component="span" sx={{ color: UI.ink3, flexShrink: 0 }}>
      {label}
    </Box>
    <Box
      component="span"
      sx={{
        color: UI.ink,
        fontWeight: 500,
        textAlign: "right",
        minWidth: 0,
        overflowWrap: "anywhere",
      }}
    >
      {value}
    </Box>
  </Box>
);

/** "‹ Back to X" link above a detail page title. */
export const BackLink = ({ children, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      border: 0,
      bgcolor: "transparent",
      padding: 0,
      marginBottom: "14px",
      color: UI.p,
      fontSize: 12,
      fontFamily: "inherit",
      cursor: "pointer",
      "&:hover": { textDecoration: "underline" },
    }}
  >
    {children}
  </Box>
);

/** Search field in the prototype's input style. */
export const SearchBox = ({
  value,
  onChange,
  placeholder = "Search",
  width = 280,
}) => (
  <Box
    component="input"
    value={value}
    placeholder={placeholder}
    onChange={onChange}
    sx={{
      border: "1px solid",
      borderColor: UI.lineIn,
      borderRadius: "4px",
      padding: "7px 11px",
      bgcolor: UI.surface,
      color: UI.ink,
      fontSize: 13,
      fontFamily: "inherit",
      outline: "none",
      boxSizing: "border-box",
      width: { xs: "100%", sm: width },
      "&::placeholder": { color: UI.ink4 },
      "&:focus": {
        borderColor: UI.p,
        boxShadow: "inset 0 0 0 1px rgba(135, 78, 246, 0.9)",
      },
    }}
  />
);

/** Toolbar that sits above a table: filters on the left, search on the right. */
export const Toolbar = ({ children, right }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: { xs: "column", sm: "row" },
      justifyContent: "space-between",
      alignItems: { xs: "stretch", sm: "center" },
      gap: "12px",
      marginBottom: "14px",
    }}
  >
    <Box sx={{ minWidth: 0 }}>{children}</Box>
    {right ? <Box sx={{ flexShrink: 0 }}>{right}</Box> : null}
  </Box>
);

/** Stacked card used where a table would overflow on a phone. */
export const RowCard = ({ children, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      bgcolor: UI.surface,
      border: "1px solid",
      borderColor: UI.line,
      borderRadius: "8px",
      padding: "13px 15px",
      cursor: onClick ? "pointer" : "default",
      "&:hover": onClick ? { borderColor: UI.p } : undefined,
    }}
  >
    {children}
  </Box>
);

/** Label and value on one line, as used inside RowCard. */
export const KV = ({ label, value }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: "10px",
      fontSize: 12,
      padding: "3px 0",
    }}
  >
    <Box component="span" sx={{ color: UI.ink3, flexShrink: 0 }}>
      {label}
    </Box>
    <Box component="span" sx={{ color: UI.ink, textAlign: "right", minWidth: 0 }}>
      {value}
    </Box>
  </Box>
);

/** Kanban board, the prototype's .board / .col / .card */
export const Board = ({ columns, onCardClick }) => (
  <Box
    sx={{
      display: "grid",
      gap: "12px",
      overflowX: "auto",
      paddingBottom: "8px",
      marginBottom: "28px",
      gridTemplateColumns: `repeat(${columns.length}, minmax(190px, 1fr))`,
    }}
  >
    {columns.map((col) => (
      <Box
        key={col.title}
        sx={{
          bgcolor: UI.surface,
          border: "1px solid",
          borderColor: UI.line,
          borderRadius: "8px",
          minWidth: 190,
        }}
      >
        <Box
          sx={{
            padding: "11px 13px",
            borderBottom: "1px solid",
            borderBottomColor: UI.line,
            bgcolor: UI.bg2,
            borderRadius: "8px 8px 0 0",
          }}
        >
          <Box
            sx={{
              fontSize: 12,
              fontWeight: 500,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              color: UI.ink,
            }}
          >
            <span>{col.title}</span>
            <span>{col.cards.length}</span>
          </Box>
          <Box sx={{ fontSize: 11, color: UI.ink3, marginTop: "2px" }}>
            {col.value}
          </Box>
        </Box>
        <Box sx={{ padding: "9px" }}>
          {col.cards.length === 0 ? (
            <Box sx={{ fontSize: 11, color: UI.ink4, padding: "6px" }}>
              Empty
            </Box>
          ) : (
            col.cards.map((c) => (
              <Box
                key={c.id}
                onClick={onCardClick ? () => onCardClick(c.id) : undefined}
                sx={{
                  border: "1px solid",
                  borderColor: UI.line,
                  borderRadius: "4px",
                  padding: "10px",
                  marginBottom: "8px",
                  cursor: onCardClick ? "pointer" : "default",
                  "&:hover": onCardClick
                    ? { borderColor: UI.p, bgcolor: UI.p50 }
                    : undefined,
                  "&:last-child": { marginBottom: 0 },
                }}
              >
                <Box sx={{ fontFamily: UI.mono, fontSize: 11, color: UI.p }}>
                  {c.ref}
                </Box>
                <Box
                  sx={{ fontSize: 12, fontWeight: 500, margin: "3px 0", color: UI.ink }}
                >
                  {c.title}
                </Box>
                <Box sx={{ fontSize: 11, color: UI.ink3 }}>{c.meta}</Box>
              </Box>
            ))
          )}
        </Box>
      </Box>
    ))}
  </Box>
);

/** Card grid, the prototype's .grid3 / .gc */
export const CardGrid = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(268px, 1fr))",
      gap: "14px",
      marginBottom: "28px",
    }}
  >
    {children}
  </Box>
);

export const GridCard = ({ title, tag, lines, actions }) => (
  <Box
    sx={{
      bgcolor: UI.surface,
      border: "1px solid",
      borderColor: UI.line,
      borderRadius: "8px",
      padding: "13px 15px",
    }}
  >
    <Box
      sx={{
        fontSize: 13,
        fontWeight: 500,
        display: "flex",
        justifyContent: "space-between",
        gap: "8px",
        alignItems: "flex-start",
        color: UI.ink,
      }}
    >
      <span>{title}</span>
      {tag}
    </Box>
    <Box
      sx={{ fontSize: 12, color: UI.ink3, margin: "5px 0 11px", lineHeight: 1.6 }}
    >
      {lines}
    </Box>
    {actions ? <Box sx={{ display: "flex", gap: "7px" }}>{actions}</Box> : null}
  </Box>
);

/**
 * columns: [{ label, num }]
 * rows:    [{ id, cells: [] }]
 */
const DashTable = ({
  columns,
  rows,
  onRowClick,
  foot,
  emptyText = "Nothing here yet.",
  errorText,
  onRetry,
}) => (
  <Box
    sx={{
      border: "1px solid",
      borderColor: UI.line,
      borderRadius: "8px",
      overflow: "hidden",
      marginBottom: "28px",
      bgcolor: UI.surface,
    }}
  >
    {/* DESKTOP/TABLET: the table itself. Hidden below sm — a table's columns
        don't reflow, they just get truncated or force horizontal scroll,
        neither of which is usable on a phone. */}
    <Box sx={{ display: { xs: "none", sm: "block" } }}>
      <Box sx={{ overflowX: "auto" }}>
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
          <Box component="thead">
            <Box component="tr">
              {columns.map((c, i) => (
                <Box
                  component="th"
                  key={c.label || i}
                  sx={{
                    textAlign: c.num ? "right" : "left",
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: ".5px",
                    color: UI.ink3,
                    fontWeight: 500,
                    padding: "10px 14px",
                    bgcolor: UI.bg2,
                    borderBottom: "1px solid",
                    borderBottomColor: UI.line,
                    whiteSpace: "nowrap",
                  }}
                >
                  {c.label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {rows.length === 0 ? (
              <Box component="tr">
                <Box component="td" colSpan={columns.length}>
                  <Box
                    sx={{
                      padding: "26px",
                      textAlign: "center",
                      color: UI.ink3,
                      fontSize: 13,
                    }}
                  >
                    {errorText || emptyText}
                    {errorText && onRetry ? (
                      <Box
                        component="button"
                        type="button"
                        onClick={onRetry}
                        sx={{
                          ...btnSx("ghost"),
                          display: "block",
                          margin: "10px auto 0",
                        }}
                      >
                        Retry
                      </Box>
                    ) : null}
                  </Box>
                </Box>
              </Box>
            ) : (
              rows.map((r, ri) => {
                const clickable = Boolean(r.id && onRowClick);
                return (
                  <Box
                    component="tr"
                    key={r.key || r.id || ri}
                    onClick={clickable ? () => onRowClick(r.id) : undefined}
                    sx={{
                      cursor: clickable ? "pointer" : "default",
                      "&:hover": clickable ? { bgcolor: UI.p50 } : undefined,
                      "&:last-child td": { borderBottom: 0 },
                    }}
                  >
                    {r.cells.map((cell, ci) => (
                      <Box
                        component="td"
                        key={ci}
                        sx={{
                          padding: "11px 14px",
                          borderBottom: "1px solid",
                          borderBottomColor: UI.line,
                          fontSize: 13,
                          verticalAlign: "top",
                          color: UI.ink,
                          textAlign: columns[ci]?.num ? "right" : "left",
                          fontVariantNumeric: columns[ci]?.num
                            ? "tabular-nums"
                            : undefined,
                          whiteSpace: columns[ci]?.num ? "nowrap" : undefined,
                        }}
                      >
                        {cell}
                      </Box>
                    ))}
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      </Box>
    </Box>

    {/* MOBILE: same columns/rows, presented as stacked cards instead of a
        table — automatic for every DashTable consumer, no per-page work.
        First cell is shown as the card's heading; the rest as label/value
        rows keyed off each column's own label. */}
    <Box sx={{ display: { xs: "flex", sm: "none" }, flexDirection: "column", gap: "10px", padding: "12px" }}>
      {rows.length === 0 ? (
        <Box sx={{ padding: "16px 4px", textAlign: "center", color: UI.ink3, fontSize: 13 }}>
          {errorText || emptyText}
          {errorText && onRetry ? (
            <Box
              component="button"
              type="button"
              onClick={onRetry}
              sx={{ ...btnSx("ghost"), display: "block", margin: "10px auto 0" }}
            >
              Retry
            </Box>
          ) : null}
        </Box>
      ) : (
        rows.map((r, ri) => {
          const clickable = Boolean(r.id && onRowClick);
          return (
            <RowCard key={r.key || r.id || ri} onClick={clickable ? () => onRowClick(r.id) : undefined}>
              {r.cells.map((cell, ci) =>
                ci === 0 ? (
                  <Box key={ci} sx={{ fontSize: 13, fontWeight: 500, color: UI.ink, marginBottom: "6px" }}>
                    {cell}
                  </Box>
                ) : (
                  <KV key={ci} label={columns[ci]?.label} value={cell} />
                ),
              )}
            </RowCard>
          );
        })
      )}
    </Box>
    {foot ? (
      <Box
        sx={{
          padding: "10px 14px",
          bgcolor: UI.bg2,
          borderTop: "1px solid",
          borderTopColor: UI.line,
          fontSize: 12,
          color: UI.ink3,
        }}
      >
        {foot}
      </Box>
    ) : null}
  </Box>
);

export default DashTable;
